// My Support Tickets list (Story #4613, Stream 8b) — mockup 02. Filter segmented
// control + waiting-on-you banner + table (Reference / Subject / Type / Status /
// Last update). Requester-facing: never exposes cross-ticket info.

import { useMemo, useState } from 'react'
import { Plus } from 'lucide-react'
import type { DisplayStatus } from '../../constants/support'
import type { SupportTicket } from '../../types/support'
import { deriveDisplayStatus, formatAedt } from '../../utils/supportDisplay'
import { DisplayStatusChip, RequestTypeBadge } from './SupportBadges'

type Filter = 'all' | 'open' | 'waiting' | 'resolved' | 'closed'

const FILTERS: { value: Filter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'open', label: 'Open' },
  { value: 'waiting', label: 'Waiting on you' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'closed', label: 'Closed' },
]

function matchesFilter(filter: Filter, status: DisplayStatus): boolean {
  switch (filter) {
    case 'all':
      return true
    case 'open':
      return status === 'new' || status === 'in_progress' || status === 'waiting'
    case 'waiting':
      return status === 'waiting'
    case 'resolved':
      return status === 'resolved'
    case 'closed':
      return status === 'closed'
  }
}

interface MyTicketsListProps {
  tickets: SupportTicket[]
  requesterUserId: string | undefined
  onOpen: (ticketId: string) => void
  onNew: () => void
}

export default function MyTicketsList({ tickets, requesterUserId, onOpen, onNew }: MyTicketsListProps) {
  const [filter, setFilter] = useState<Filter>('all')

  const withStatus = useMemo(
    () => tickets.map((t) => ({ t, status: deriveDisplayStatus(t, requesterUserId) })),
    [tickets, requesterUserId],
  )
  const waiting = withStatus.filter((x) => x.status === 'waiting').map((x) => x.t)
  const visible = withStatus.filter((x) => matchesFilter(filter, x.status))

  return (
    <div>
      {waiting.length > 0 && (
        <div className="mb-3.5 rounded-lg border border-amber-200 bg-amber-50 px-3.5 py-2.5 text-[12.5px] text-amber-800">
          <b>
            {waiting[0].ref} is waiting on you
          </b>{' '}
          — support asked a question. Tickets waiting on you close automatically after 14 days.
        </div>
      )}

      <div className="mb-3 flex flex-wrap items-center gap-2 rounded-xl border border-iflow-900/10 bg-white/70 px-3 py-2.5">
        <div className="inline-flex overflow-hidden rounded-md border border-slate-300">
          {FILTERS.map((f) => (
            <button
              key={f.value}
              type="button"
              onClick={() => setFilter(f.value)}
              className={`border-r border-slate-200 px-3 py-1.5 text-[12.5px] last:border-r-0 ${
                filter === f.value ? 'bg-iflow-500 font-semibold text-white' : 'bg-white text-slate-700 hover:bg-slate-50'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div className="flex-1" />
        <button
          type="button"
          onClick={onNew}
          className="inline-flex items-center gap-1.5 rounded-lg bg-iflow-500 px-3 py-1.5 text-[12.5px] font-semibold text-white hover:bg-iflow-600"
        >
          <Plus size={14} /> New support request
        </button>
      </div>

      <div className="overflow-x-auto rounded-xl border border-iflow-900/10">
        <table className="w-full min-w-[640px] border-collapse bg-white text-left text-[12.5px]">
          <thead>
            <tr className="bg-iflow-900 text-[11.5px] uppercase tracking-wide text-white">
              <th className="px-3 py-2.5 font-semibold">Reference</th>
              <th className="px-3 py-2.5 font-semibold">Subject</th>
              <th className="px-3 py-2.5 font-semibold">Type</th>
              <th className="px-3 py-2.5 font-semibold">Status</th>
              <th className="px-3 py-2.5 font-semibold">Last update (AEDT)</th>
            </tr>
          </thead>
          <tbody>
            {visible.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-3 py-10 text-center text-slate-500">
                  No tickets in this view.
                </td>
              </tr>
            ) : (
              visible.map(({ t, status }) => (
                <tr
                  key={t.ticket_id}
                  onClick={() => onOpen(t.ticket_id)}
                  className="cursor-pointer border-b border-slate-100 hover:bg-iflow-50"
                >
                  <td className="px-3 py-2.5 font-semibold text-iflow-600">{t.ref}</td>
                  <td className="px-3 py-2.5 text-slate-800">{t.subject}</td>
                  <td className="px-3 py-2.5">
                    <RequestTypeBadge type={t.request_type} />
                  </td>
                  <td className="px-3 py-2.5">
                    <DisplayStatusChip status={status} />
                  </td>
                  <td className="px-3 py-2.5 text-slate-500">{formatAedt(t.updated_at)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
